"use client";

import { cn } from "@repo/ui/utils";
import { Overline } from "@repo/ui/overline";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@repo/ui/accordion";
import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";
import { getI18nString, getI18nValue } from "@/lib/utils";
import type { BodyListRevealProps } from "./body-list-reveal.types";

export function BodyListReveal({
  overline,
  headline,
  body,
  items,
  className,
}: BodyListRevealProps) {
  const bodyValue = getI18nValue(body);

  return (
    <section
      className={cn("bg-surface-base-primary", className)}
      data-testid="body-list-reveal"
    >
      <div className="container-grid py-16 desktop:py-20">
        <div className="col-span-full grid grid-cols-subgrid">
          <div className="col-span-full tablet:col-span-4 flex flex-col gap-y-4 desktop:gap-y-6">
            {overline && <Overline>{overline}</Overline>}

            {headline && (
              <h2 className="typography-mobile-heading-h5 desktop:typography-desktop-heading-h5 text-text-primary">
                {headline}
              </h2>
            )}

            {bodyValue && (
              <RichTextCondensed
                richText={bodyValue}
                className="text-text-secondary"
              />
            )}
          </div>

          {items.length > 0 && (
            <div className="col-span-full tablet:col-span-4 desktop:col-span-7 desktop:col-start-6 mt-8 tablet:mt-0">
              <Accordion
                type="single"
                collapsible
                defaultValue={items[0]?._key}
              >
                {items.map((item, index) => {
                  const itemHeadline = getI18nString(item.headline);
                  const itemBody = getI18nValue(item.bodyCopy);

                  return (
                    <AccordionItem key={item._key} value={item._key}>
                      <AccordionTrigger className="typography-mobile-heading-subtitle-1 desktop:typography-desktop-heading-subtitle-1 text-text-primary [&>svg]:hidden py-5 desktop:py-[24px]">
                        <span className="flex items-start gap-4">
                          <span className="typography-body-sm-medium text-text-secondary tabular-nums min-w-[2rem] desktop:w-[57px] desktop:min-w-[57px]">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          <span>{itemHeadline}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="[&>div]:pb-4 [&>div]:desktop:pb-5">
                        {itemBody && (
                          <div className="pl-[calc(2rem+1rem)] desktop:pl-[calc(57px+1rem)]">
                            <RichTextCondensed
                              richText={itemBody}
                              className="text-text-secondary"
                            />
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
